import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  createBillingCheckoutSession,
  createBillingPortalSession,
  deleteCompany,
  getBillingStatus,
  getMyCompanyAdminEmails,
  getUserProfile,
  updateCompanySettings,
} from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';
import '../../styles/dashboard-pages.css';
import '../../styles/onboarding.css';

const APP_BASE_URL = process.env.REACT_APP_APP_URL || 'https://risqly.net';

function CompanySettings() {
  const { logout, user } = useAuth0();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [adminControlled, setAdminControlled] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminEmailError, setAdminEmailError] = useState('');
  const [adminEmails, setAdminEmails] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [companyProfile, setCompanyProfile] = useState({
    description: '',
    industry: '',
    country: '',
  });
  const [pendingSwitch, setPendingSwitch] = useState(null); // 'domain' | 'admin' | null
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingAction, setBillingAction] = useState('');
  const [billingError, setBillingError] = useState('');
  const [billingInterval, setBillingInterval] = useState('monthly');
  const [showDeleteCompanyDialog, setShowDeleteCompanyDialog] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingProfile(true);
        const profile = await getUserProfile();
        if (!mounted) return;
        const company = profile?.company || {};
        setCompanyProfile({
          description: company.description || '',
          industry: company.industry || '',
          country: company.country || '',
        });
        setAdminControlled(!!company.admin_controlled);
      } catch (err) {
        console.error('Failed to fetch company profile', err);
        if (mounted) {
          setProfileError(err.response?.data?.error || err.message || 'Failed to load company profile');
        }
      } finally {
        if (mounted) setLoadingProfile(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    getBillingStatus()
      .then((status) => {
        if (mounted) {
          setBilling(status);
          if (!status.available_billing_intervals?.includes('monthly')) {
            setBillingInterval(status.available_billing_intervals?.[0] || 'monthly');
          }
        }
      })
      .catch((err) => {
        if (mounted) setBillingError(err.response?.data?.error || 'Failed to load billing');
      })
      .finally(() => {
        if (mounted) setBillingLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingAdmins(true);
        const list = await getMyCompanyAdminEmails();
        if (!mounted) return;
        const userEmail = user?.email || '';
        // Split out current user from other admins (onboarding UI shows current user separately)
        const others = (list || []).filter(e => e !== userEmail);
        setAdminEmails(others);
      } catch (err) {
        console.error('Failed to fetch admin emails', err);
      } finally {
        if (mounted) setLoadingAdmins(false);
      }
    })();
    return () => { mounted = false; };
  }, [user]);

  const handleDeleteCompany = async () => {
    if (isDeleting) return;

    try {
      setError('');
      setIsDeleting(true);
      await deleteCompany();
      logout({ returnTo: APP_BASE_URL });
    } catch (err) {
      console.error('Failed to delete company:', err);
      setError(err.response?.data?.error || 'Failed to delete company. Please try again.');
      setIsDeleting(false);
    }
  };

  // helper to remove an admin email is used in UI

  const handleRemoveAdminEmail = (idx) => {
    (async () => {
      const copy = [...adminEmails];
      copy.splice(idx, 1);
      // If not admin-controlled, just update local state
      if (!adminControlled) {
        setAdminEmails(copy);
        return;
      }

      // Persist removal to server: ensure requester remains included
      setSaving(true);
      setError('');
      try {
        const normalized = copy.map(e => String(e || '').trim().toLowerCase()).filter(Boolean);
        const requester = String(user?.email || '').trim().toLowerCase();
        if (requester && !normalized.includes(requester)) normalized.unshift(requester);
        await updateCompanySettings({ admin_controlled: true, admin_emails: normalized });
        // Update local list excluding requester
        const others = normalized.filter(e => e !== requester);
        setAdminEmails(others);
      } catch (err) {
        console.error('Failed to remove admin email', err);
        setError(err.response?.data?.error || err.message || 'Failed to update admin list');
      } finally {
        setSaving(false);
      }
    })();
  };
  
  const addAdminEmail = () => {
    setAdminEmailError('');
    const val = String(newAdminEmail || '').trim().toLowerCase();
    if (!val) {
      setAdminEmailError('Please enter an email address');
      return;
    }
    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(val)) {
      setAdminEmailError('Please enter a valid email address');
      return;
    }
    if (adminEmails.includes(val)) {
      setAdminEmailError('This email has already been added');
      return;
    }
    const userEmail = user?.email || '';
    if (val === userEmail) {
      setAdminEmailError('Your email is already the admin');
      return;
    }
    setAdminEmails([...adminEmails, val]);
    setNewAdminEmail('');
  };

  const updateProfileField = (field, value) => {
    setProfileSuccess('');
    setProfileError('');
    setCompanyProfile(current => ({
      ...current,
      [field]: value,
    }));
  };

  const normalizeProfileValue = (value) => {
    const trimmed = String(value || '').trim();
    return trimmed || null;
  };

  const handleSaveProfile = async () => {
    try {
      setSavingProfile(true);
      setProfileError('');
      setProfileSuccess('');
      const updated = await updateCompanySettings({
        description: normalizeProfileValue(companyProfile.description),
        industry: normalizeProfileValue(companyProfile.industry),
        country: normalizeProfileValue(companyProfile.country),
      });
      setCompanyProfile({
        description: updated?.description || '',
        industry: updated?.industry || '',
        country: updated?.country || '',
      });
      setAdminControlled(!!updated?.admin_controlled);
      setProfileSuccess('Company profile updated');
    } catch (err) {
      console.error('Failed to save company profile', err);
      setProfileError(err.response?.data?.error || err.message || 'Failed to save company profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError('');
      // Normalize emails and ensure requester is included when enabling admin control
      const normalized = adminEmails.map(e => String(e || '').trim().toLowerCase()).filter(Boolean);
      const requester = String(user?.email || '').trim().toLowerCase();
      if (adminControlled && requester && !normalized.includes(requester)) {
        normalized.unshift(requester);
      }
      await updateCompanySettings({ admin_controlled: !!adminControlled, admin_emails: normalized });
    } catch (err) {
      console.error('Failed to save company settings', err);
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-settings-page">
      <div className="page-header">
        <h2>Company Settings</h2>
        <p>Manage your public profile details, dashboard access, and irreversible account actions.</p>
      </div>

      <div className="settings-layout">
        <section className="settings-panel billing-settings-panel">
          <div className="settings-section-heading">
            <span className="material-symbols-outlined">workspace_premium</span>
            <div>
              <h3>Plan and Billing</h3>
              <p>Manage your company&apos;s Risqly plan through Stripe.</p>
            </div>
          </div>

          {billingLoading ? (
            <div className="settings-loading">Loading billing status...</div>
          ) : (
            <>
              <div className="billing-plan-summary">
                <div>
                  <span>Current plan</span>
                  <strong>
                    {billing?.has_intelligence_access ? 'Risqly Intelligence' : 'Risqly Free'}
                  </strong>
                </div>
                <span className={`billing-plan-badge ${billing?.has_intelligence_access ? 'is-premium' : ''}`}>
                  {billing?.subscription?.status || 'free'}
                </span>
              </div>

              {billing?.subscription?.cancel_at_period_end && billing?.subscription?.current_period_end && (
                <p className="billing-period-note">
                  Intelligence remains active until{' '}
                  {new Date(billing.subscription.current_period_end).toLocaleDateString()}.
                </p>
              )}

              {!billing?.has_intelligence_access && (
                <>
                  <div className="billing-feature-list">
                    <span>Security Insights and prioritized guidance</span>
                    <span>Detailed external scan evidence</span>
                    <span>Recurring external integrity scans</span>
                    <span>14-day free trial</span>
                  </div>
                  {billing?.stripe_configured && (
                    <div className="billing-interval-toggle" aria-label="Billing interval">
                      {['monthly', 'annual'].map((interval) => (
                        <button
                          type="button"
                          key={interval}
                          className={billingInterval === interval ? 'is-active' : ''}
                          disabled={!billing.available_billing_intervals?.includes(interval)}
                          onClick={() => setBillingInterval(interval)}
                        >
                          {interval === 'monthly' ? 'Monthly' : 'Annual'}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="settings-actions">
                {billing?.can_manage_billing ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={Boolean(billingAction)}
                    onClick={async () => {
                      try {
                        setBillingAction('portal');
                        setBillingError('');
                        window.location.assign(await createBillingPortalSession());
                      } catch (err) {
                        setBillingError(err.response?.data?.error || 'Failed to open billing portal');
                        setBillingAction('');
                      }
                    }}
                  >
                    {billingAction === 'portal' ? 'Opening...' : 'Manage Billing'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={Boolean(billingAction) || !billing?.stripe_configured}
                    onClick={async () => {
                      try {
                        setBillingAction('checkout');
                        setBillingError('');
                        window.location.assign(await createBillingCheckoutSession(billingInterval));
                      } catch (err) {
                        setBillingError(err.response?.data?.error || 'Checkout is not available yet');
                        setBillingAction('');
                      }
                    }}
                  >
                    {billingAction === 'checkout'
                      ? 'Opening checkout...'
                      : billing?.stripe_configured
                        ? 'Start Intelligence Trial'
                        : 'Checkout coming soon'}
                  </button>
                )}
              </div>
              {billingError && <div className="error-text">{billingError}</div>}
            </>
          )}
        </section>

        <section className="settings-panel">
          <div className="settings-section-heading">
            <span className="material-symbols-outlined">domain</span>
            <div>
              <h3>Company Profile</h3>
              <p>Keep your public company details current across Risqly.</p>
            </div>
          </div>

          {loadingProfile ? (
            <div className="settings-loading">Loading company profile...</div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="company-description">Company Description</label>
                <textarea
                  id="company-description"
                  className="form-textarea"
                  value={companyProfile.description}
                  onChange={(e) => updateProfileField('description', e.target.value)}
                  placeholder="Briefly describe what your company does"
                  rows={4}
                />
              </div>

              <div className="company-profile-grid">
                <div className="form-group">
                  <label htmlFor="company-industry">Industry</label>
                  <input
                    id="company-industry"
                    type="text"
                    className="form-input"
                    value={companyProfile.industry}
                    onChange={(e) => updateProfileField('industry', e.target.value)}
                    placeholder="Software, Finance, Healthcare..."
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company-country">Country</label>
                  <input
                    id="company-country"
                    type="text"
                    className="form-input"
                    value={companyProfile.country}
                    onChange={(e) => updateProfileField('country', e.target.value)}
                    placeholder="Sweden"
                  />
                </div>
              </div>

              {profileError && <div className="error-text">{profileError}</div>}
              {profileSuccess && <div className="success-text">{profileSuccess}</div>}

              <div className="settings-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveProfile}
                  disabled={savingProfile}
                >
                  {savingProfile ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </>
          )}
        </section>

        <section className="settings-panel">
          <div className="settings-section-heading">
            <span className="material-symbols-outlined">admin_panel_settings</span>
            <div>
              <h3>Access Controls</h3>
              <p>Choose who can enter your company dashboard and manage the authorized admin list.</p>
            </div>
          </div>

        <div className="form-group">
          <label>Access Control</label>
          <div className="access-control-toggle">
            <button
              type="button"
              className={`toggle-option ${!adminControlled ? 'active' : ''} ${pendingSwitch === 'domain' ? 'pending' : ''}`}
              onClick={() => {
                if (!adminControlled) return; // already domain-limited
                setPendingSwitch('domain');
                setAdminEmailError('');
              }}
            >
              <span className="material-symbols-outlined">security</span>
              Domain-Limited Access
            </button>
            <button
              type="button"
              className={`toggle-option ${adminControlled ? 'active' : ''} ${pendingSwitch === 'admin' ? 'pending' : ''}`}
              onClick={() => {
                if (adminControlled) return; // already admin-controlled
                setPendingSwitch('admin');
                setAdminEmailError('');
              }}
            >
              <span className="material-symbols-outlined">supervised_user_circle</span>
              Admin-Controlled Access
            </button>
          </div>

          {pendingSwitch && (
            <div className="settings-confirm-row">
              <div>
                <strong>Confirm switch</strong>
                <div>
                  Are you sure you want to switch to <em>{pendingSwitch === 'domain' ? 'Domain-Limited Access' : 'Admin-Controlled Access'}</em>? This will update your company settings.
                </div>
              </div>
              <div className="settings-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => setPendingSwitch(null)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={async () => {
                    setSaving(true);
                    setError('');
                    try {
                      if (pendingSwitch === 'domain') {
                        await updateCompanySettings({ admin_controlled: false, admin_emails: [] });
                        setAdminControlled(false);
                        setAdminEmails([]);
                      } else {
                        // enabling admin controlled: include requester email
                        const normalized = adminEmails.map(e => String(e || '').trim().toLowerCase()).filter(Boolean);
                        const requester = String(user?.email || '').trim().toLowerCase();
                        if (requester && !normalized.includes(requester)) normalized.unshift(requester);
                        await updateCompanySettings({ admin_controlled: true, admin_emails: normalized });
                        // Refresh admin emails from server so invited admins appear immediately
                        try {
                          setLoadingAdmins(true);
                          const list = await getMyCompanyAdminEmails();
                          const others = (list || []).filter(e => e !== requester);
                          setAdminEmails(others);
                        } catch (fetchErr) {
                          console.error('Failed to refresh admin emails', fetchErr);
                        } finally {
                          setLoadingAdmins(false);
                        }
                        setAdminControlled(true);
                      }
                      setPendingSwitch(null);
                    } catch (err) {
                      console.error('Failed to switch access mode', err);
                      setError(err.response?.data?.error || err.message || 'Failed to update settings');
                    } finally {
                      setSaving(false);
                    }
                  }}
                >
                  {saving ? 'Saving...' : 'Yes, switch'}
                </button>
              </div>
            </div>
          )}
          <small>
            {adminControlled
              ? 'Only users you specify can access the dashboard'
              : 'Anyone with your company domain email can access the dashboard'}
          </small>
        </div>

        {adminControlled && (
          <div className="form-group">
            <label>Authorized Admins</label>

            {/* Current user */}
            <div className="admin-email-item current-user">
              <div className="email-content">
                <span className="email">{user?.email}</span>
                <span className="badge">You (Admin)</span>
              </div>
            </div>

            {/* List of other admins */}
            {loadingAdmins ? (
              <div>Loading admins...</div>
            ) : (
              <>
                {adminEmails.length > 0 && (
                  <div className="admin-emails-list">
                    {adminEmails.map((email, idx) => (
                      <div key={email} className="admin-email-item">
                        <div className="email-content">
                          <span className="email">{email}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAdminEmail(idx)}
                          className="btn-remove-email"
                        >
                          <span className="material-symbols-outlined">close</span>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="add-admin-email-group">
                  <input
                    type="email"
                    value={newAdminEmail}
                    onChange={(e) => { setNewAdminEmail(e.target.value); setAdminEmailError(''); }}
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAdminEmail(); } }}
                    placeholder="Enter admin email"
                    className="form-input"
                  />
                  <button type="button" onClick={addAdminEmail} className="btn btn-secondary">Add</button>
                </div>

                <div className="settings-actions settings-actions-spaced">
                  <button className="btn btn-primary" onClick={handleSaveSettings} disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
                </div>

                {(adminEmailError || error) && <div className="error-text">{adminEmailError || error}</div>}
              </>
            )}
          </div>
        )}
        </section>

        <section className="settings-panel danger-zone-card">
          <div className="settings-section-heading danger-zone-header">
            <span className="material-symbols-outlined">warning</span>
            <div>
              <h3>Danger Zone</h3>
              <p>Delete your company page and all related Risqly data.</p>
            </div>
          </div>
          <p className="danger-zone-text">
            This includes users, policy documents, scores, activity logs, and vendor relationships.
          </p>

          {error && <p className="danger-zone-error">{error}</p>}

          <button
            className="btn btn-secondary btn-danger"
            onClick={() => setShowDeleteCompanyDialog(true)}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Company and Data'}
          </button>
        </section>
      </div>

      <ConfirmDialog
        open={showDeleteCompanyDialog}
        title="Permanently delete company?"
        description="Your company page, users, policy documents, scores, activity logs, and vendor relationships will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete Company and Data"
        confirmationText="DELETE"
        loading={isDeleting}
        onConfirm={handleDeleteCompany}
        onCancel={() => setShowDeleteCompanyDialog(false)}
      />
    </div>
  );
}

export default CompanySettings;
